import { useSanity } from '#imports';
import { useNuxtApp } from '#app';
import { withSsrDependencyTiming as recordSsrDependencyTiming } from '@/lib/ssr-timing';
import type { SiteLocale, TranslationAlternate } from '@/lib/site-locale';

export interface BlogListConfig {
  id?: string;
  language: SiteLocale;
  exists: boolean;
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  title: string;
  subtitle: string;
  featuredArticleSlugs: string[];
}

interface RawBlogListConfig {
  _id?: string;
  language?: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  title?: string;
  subtitle?: string;
  featuredArticleSlugs?: string[];
}

export function orderBlogPostsByFeaturedSlugs<T extends { slug: string }>(
  posts: T[],
  featuredArticleSlugs: string[]
): T[] {
  if (!featuredArticleSlugs.length) return posts;
  const priority = new Map(featuredArticleSlugs.map((slug, index) => [slug, index]));
  return posts
    .map((post, index) => ({ post, index, priority: priority.get(post.slug) }))
    .sort((left, right) => {
      if (left.priority === undefined && right.priority === undefined) {
        return left.index - right.index;
      }
      if (left.priority === undefined) return 1;
      if (right.priority === undefined) return -1;
      return left.priority - right.priority;
    })
    .map(({ post }) => post);
}

/**
 * Type definitions for blog post data structures
 */
interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  image?: string;
  imageAlt?: string;
  publishedAt?: string;
  _updatedAt?: string;
  category?: string;
  categories?: Array<{ _id?: string; title: string }>; // Legacy support
  categoryRefs?: string[]; // Legacy support
  tags?: string[];
  readTime?: number;
  faq?: Array<{ question: string; answer: string }>;
  youtubeLinks?: Array<{ url: string; title?: string }>;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  bodyContent?: ContentBlock[];
  body?: any[];
  relatedPosts?: RelatedPost[];
  language?: SiteLocale;
  translationKey?: string;
  alternates?: TranslationAlternate[];
}

interface RelatedPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  image?: string;
  publishedAt?: string;
}

interface ContentBlock {
  _type: string;
  _key: string;
  url?: string | null;
  title?: string;
  caption?: string;
  [key: string]: any;
}

/**
 * Composable for fetching blog posts from Sanity
 */
export const useBlog = () => {
  const $sanity = useSanity();
  const nuxtApp = useNuxtApp();
  const withSsrDependencyTiming = <T>(
    label: string,
    run: () => Promise<T>,
    meta: { url?: string; kind?: 'internal' | 'external'; status?: number } = {}
  ): Promise<T> => nuxtApp.runWithContext(() => recordSsrDependencyTiming(label, run, meta));

  /**
   * Full fields for single learn post
   *
   * CRITICAL: Uses GROQ conditional projection to explicitly declare bodyYoutube fields
   * This ensures url, title, caption fields are ALWAYS returned, even if null/undefined
   *
   * Syntax: bodyContent[]{ ..., _type == "bodyYoutube" => { explicit fields } }
   *
   * Benefits:
   * - Guarantees field existence in response
   * - Prevents field filtering by Sanity client
   * - Stable and reliable data structure
   */
  const learnPostFields = `
    _id,
    "slug": slug.current,
    title,
    // Support both Portable Text array and legacy string format
    // Query both formats and handle in code
    "excerptArray": excerpt[]{
      ...,
      markDefs[]{
        ...,
        _type == "link" => {
          ...,
          "href": href,
          "target": target
        }
      },
      _type == "image" => {
        _type,
        _key,
        alt,
        crop,
        hotspot,
        "url": asset->url
      }
    },
    "excerptString": excerpt,
    bodyContent[]{
      _key,
      _type,
      // Common fields for all block types
      text,
      bold,
      links[]{
        text,
        url,
        target
      },
      level,
      listType,
      items[]{
        text,
        bold,
        links[]{
          text,
          url,
          target
        }
      },
      // YouTube-specific fields (for bodyYoutube blocks)
      url,
      title,
      caption,
      // Image-specific fields (for bodyImage blocks)
      "src": coalesce(src, image.asset->url),
      alt,
      imageQuery
    },
    "image": image.asset->url,
    "imageAlt": image.alt,
    publishedAt,
    _updatedAt,
    category,
    tags,
    readTime,
    faq[]{
      question,
      answer
    },
    youtubeLinks[]{
      url,
      title
    },
    "seoTitle": seo.seoTitle,
    "seoDescription": seo.seoDescription,
    "keywords": seo.keywords,
    "canonicalUrl": seo.canonicalUrl,
    "ogImage": seo.ogImage.asset->url
  `;

  /**
   * Validate and normalize YouTube block URL
   * Ensures URL is properly formatted and valid
   */
  const validateYouTubeBlock = (block: ContentBlock): ContentBlock => {
    const urlValue = block.url;

    // Check if URL is valid (not null, not undefined, is string, not empty)
    const isValidUrl =
      urlValue !== undefined &&
      urlValue !== null &&
      typeof urlValue === 'string' &&
      urlValue.trim().length > 0;

    if (!isValidUrl) {
      // Set url to null if invalid
      return { ...block, url: null };
    }

    // Normalize YouTube URL - extract video ID and create clean URL
    const urlMatch = urlValue.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/
    );

    if (urlMatch && urlMatch[1]) {
      return { ...block, url: `https://www.youtube.com/watch?v=${urlMatch[1]}` };
    }

    // If URL doesn't match pattern but exists and is valid, return as-is
    return { ...block, url: urlValue };
  };

  /**
   * Validate and normalize bodyContent blocks
   * Processes YouTube blocks to ensure URL validity
   */
  const validateBodyContent = (bodyContent: ContentBlock[] | undefined): ContentBlock[] => {
    if (!bodyContent || !Array.isArray(bodyContent)) {
      return [];
    }

    return bodyContent.map((block) => {
      const blockType = (block._type || '').toLowerCase();

      if (blockType === 'bodyyoutube') {
        return validateYouTubeBlock(block);
      }

      return block;
    });
  };

  /**
   * Fetch all published blog posts from the learn folder
   * Prioritizes 'learn' type, falls back to 'blog' type if needed
   */
  const fetchBlogPosts = async (locale: SiteLocale = 'en'): Promise<BlogPost[]> => {
    try {
      return await withSsrDependencyTiming(
        'api:blog:list',
        () => $fetch<BlogPost[]>('/api/blog', { query: { locale } }),
        { url: `/api/blog?locale=${locale}`, kind: 'internal' }
      );
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }
  };

  /**
   * Fetch the localized Blog/Learn list-page copy and featured article order.
   * Locale-less legacy documents are treated as English only.
   */
  const fetchBlogListConfig = async (locale: SiteLocale = 'en'): Promise<BlogListConfig> => {
    const query = `*[
      _type == "learnList" &&
      !(_id in path("drafts.**")) &&
      coalesce(language, "en") == $locale
    ] | order(_updatedAt desc)[0]{
      _id,
      "language": coalesce(language, "en"),
      seoTitle,
      seoDescription,
      "keywords": coalesce(keywords, []),
      title,
      subtitle,
      "featuredArticleSlugs": featuredArticles[]->slug.current
    }`;
    try {
      const raw = await withSsrDependencyTiming(
        'sanity:blog:list-config',
        () => $sanity.fetch<RawBlogListConfig | null>(query, { locale }),
        { url: `sanity://learn-list-config/${locale}`, kind: 'external' }
      );
      const language = ['en', 'ja', 'ko', 'es'].includes(raw?.language || '')
        ? (raw?.language as SiteLocale)
        : locale;
      return {
        id: raw?._id,
        language,
        exists: Boolean(raw?._id),
        seoTitle: raw?.seoTitle || '',
        seoDescription: raw?.seoDescription || '',
        keywords: (raw?.keywords || []).filter(Boolean),
        title: raw?.title || '',
        subtitle: raw?.subtitle || '',
        featuredArticleSlugs: (raw?.featuredArticleSlugs || []).filter(Boolean),
      };
    } catch (error) {
      console.error('Error fetching localized blog list config:', error);
      return {
        language: locale,
        exists: false,
        seoTitle: '',
        seoDescription: '',
        keywords: [],
        title: '',
        subtitle: '',
        featuredArticleSlugs: [],
      };
    }
  };

  /**
   * Fetch recommended posts by category
   * Tries to match categories first, then falls back to all posts
   */
  const fetchRecommendedPosts = async (
    categoryNames: string[] = [],
    excludeSlug?: string,
    limit: number = 4,
    locale: SiteLocale = 'en'
  ): Promise<BlogPost[]> => {
    if (limit <= 0) {
      return [];
    }

    // Query for posts matching category (category is a string field)
    const categoryQuery = `*[_type == "learn" && coalesce(language, "en") == $locale && category in $categoryNames && slug.current != $excludeSlug] | order(publishedAt desc) [0...$limit]{
      _id,
      "slug": slug.current,
      title,
      excerpt,
      "image": image.asset->url,
      publishedAt,
      category
    }`;

    // Fallback query for all posts
    const fallbackQuery = `*[_type == "learn" && coalesce(language, "en") == $locale && slug.current != $excludeSlug] | order(publishedAt desc) [0...$limit]{
      _id,
      "slug": slug.current,
      title,
      excerpt,
      "image": image.asset->url,
      publishedAt,
      category
    }`;

    try {
      // Try to get posts from same category
      let posts = await withSsrDependencyTiming(
        'sanity:blog:recommended:category',
        () =>
          $sanity.fetch<BlogPost[]>(categoryQuery, {
            categoryNames: categoryNames || [],
            excludeSlug: excludeSlug || '',
            limit,
            locale,
          }),
        { url: 'sanity://recommended-category', kind: 'external' }
      );

      // If we don't have enough posts, fetch from all posts
      if (posts.length < limit) {
        const allPosts = await withSsrDependencyTiming(
          'sanity:blog:recommended:fallback',
          () =>
            $sanity.fetch<BlogPost[]>(fallbackQuery, {
              excludeSlug: excludeSlug || '',
              limit,
              locale,
            }),
          { url: 'sanity://recommended-fallback', kind: 'external' }
        );

        // Combine and remove duplicates
        const existingSlugs = new Set(posts.map((p) => p.slug));
        const additionalPosts = allPosts.filter((p) => !existingSlugs.has(p.slug));
        posts = [...posts, ...additionalPosts].slice(0, limit);
      }

      // Shuffle results for variety
      return posts.sort(() => Math.random() - 0.5).slice(0, limit);
    } catch (error) {
      console.error('Error fetching recommended posts:', error);
      return [];
    }
  };

  /**
   * Fetch a single blog post by slug
   * Prioritizes 'learn' type, falls back to 'blog' type if needed
   */
  const fetchBlogPostBySlug = async (
    slug: string,
    locale: SiteLocale = 'en'
  ): Promise<BlogPost | null> => {
    if (!slug) {
      console.error('fetchBlogPostBySlug: slug is required');
      return null;
    }

    const learnQuery = `*[_type == "learn" && coalesce(language, "en") == $locale && slug.current == $slug][0]{
      ${learnPostFields},
      language,
      translationKey,
      "alternates": *[
        _type == "learn" &&
        defined(^.translationKey) &&
        translationKey == ^.translationKey &&
        coalesce(language, "en") in ["en", "ja", "ko", "es"]
      ]{
        "language": coalesce(language, "en"),
        "slug": slug.current
      }
    }`;

    const blogQuery = `*[_type == "blog" && coalesce(language, "en") == $locale && slug.current == $slug && status == "published" && folder == "learn"][0]{
      _id,
      "slug": slug.current,
      title,
      excerpt,
      body,
      "image": coverImage.asset->url,
      "imageAlt": coverImage.alt,
      publishedAt,
      _updatedAt,
      author->{
        name,
        bio,
        "avatar": image.asset->url
      },
      categories[]->{
        title
      },
      tags,
      "readTime": round(length(pt::text(body)) / 5 / 180),
      "seoTitle": seo.metaTitle,
      "seoDescription": seo.metaDescription,
      "keywords": seo.keywords,
      language,
      translationKey,
      "alternates": *[
        _type == "blog" &&
        status == "published" &&
        folder == "learn" &&
        defined(^.translationKey) &&
        translationKey == ^.translationKey &&
        coalesce(language, "en") in ["en", "ja", "ko", "es"]
      ]{
        "language": coalesce(language, "en"),
        "slug": slug.current
      },
      relatedPosts[]->{
        _id,
        "slug": slug.current,
        title,
        excerpt,
        "image": coverImage.asset->url,
        publishedAt
      }
    }`;

    try {
      // Try learn type first
      const learnData = await withSsrDependencyTiming(
        'sanity:blog:detail:learn',
        () => $sanity.fetch<BlogPost>(learnQuery, { slug, locale }),
        { url: `sanity://blog-detail/${locale}/${slug}`, kind: 'external' }
      );

      if (learnData) {
        // Validate and normalize bodyContent
        if (learnData.bodyContent) {
          learnData.bodyContent = validateBodyContent(learnData.bodyContent);
        }

        return learnData;
      }

      // Fallback to blog type
      const blogData = await withSsrDependencyTiming(
        'sanity:blog:detail:fallback',
        () => $sanity.fetch<BlogPost>(blogQuery, { slug, locale }),
        { url: `sanity://blog-detail-fallback/${locale}/${slug}`, kind: 'external' }
      );
      return blogData || null;
    } catch (error) {
      console.error('Error fetching blog post by slug:', error);
      return null;
    }
  };

  return {
    fetchBlogPosts,
    fetchBlogListConfig,
    fetchBlogPostBySlug,
    fetchRecommendedPosts,
  };
};
